import { createContext, useContext, useEffect, useState, useRef } from "react";
import api from "../services/api";
import { useTranslation } from "react-i18next";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const { i18n } = useTranslation();
  const fetchTimeoutRef = useRef(null);

  // Función para verificar el usuario actual
  const fetchUser = async () => {
    const token = localStorage.getItem('token');
    if (!token || token === 'null' || token === 'undefined' || token.trim() === '') {
      setUser(null);
      setLoading(false);
      return;
    }
    
    const apiLang = ["es", "en"].includes(i18n.language) ? i18n.language : "es";
    setLoading(true);
    try {
      const res = await api.get(`/${apiLang}/me`);
      setUser(res.data);
    } catch (err) {
      console.error('Error fetching user:', err);
      setUser(null);
      // Token is invalid, remove it
      localStorage.removeItem('token');
    } finally {
      setLoading(false);
    }
  };

  // Al montar, verificar si hay token válido
  useEffect(() => {
    fetchUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cuando cambia el idioma, re-verificar usuario
  useEffect(() => {
    // Evitar demasiadas peticiones durante cambios de idioma
    if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    fetchTimeoutRef.current = setTimeout(() => {
      if (localStorage.getItem('token')) {
        fetchUser();
      }
    }, 300);
    
    return () => {
      if (fetchTimeoutRef.current) clearTimeout(fetchTimeoutRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language]);

  // Escuchar cambios de token en localStorage (desde otras tabs o componentes)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        fetchUser();
      }
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const logout = async () => {
    const apiLang = ["es", "en"].includes(i18n.language) ? i18n.language : "es";
    try {
      await api.post(`/${apiLang}/logout`);
      localStorage.removeItem("token");
      setUser(null);
      return true;
    } catch {
      return false;
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, setUser, logout, fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
